/* DreamVacationPage — per-vacation detail (money jar, pin board, itinerary).
 *
 * Structurally identical to the legacy singleton DreamPage, but every write
 * is scoped to `/dream/vacations/:id/…` so a user can have as many dreams
 * as they want without the pages stepping on each other.
 *
 * The Money Jar, itinerary, and quick-add UI are copied verbatim from the
 * old singleton page so the visual + interaction language stays the same.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus, Minus, Pencil, Utensils, Footprints, NotebookPen, Trash2, ExternalLink, X, ArrowLeft, Check, Star,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import PinBoard from "@/components/PinBoard";
import { DREAM } from "@/constants/testIds";
import { toast } from "sonner";

const QUICK_ADD = [50, 100, 250, 500];
const JAR_FILL_TRANSITION = { type: "spring", stiffness: 60, damping: 18 };

/* ------------------ Money Jar SVG (copied from DreamPage) ------------------ */
function MoneyJar({ percent }) {
  const pct = Math.max(0, Math.min(100, percent));
  const Y_BOTTOM = 209;
  const Y_TOP = 62;
  const FILL_HEIGHT = (Y_BOTTOM - Y_TOP) * (pct / 100);
  const FILL_Y = Y_BOTTOM - FILL_HEIGHT;
  return (
    <svg viewBox="0 0 220 240" className="w-56 h-60 mx-auto" aria-label="Vacation Fund Jar">
      <rect x="50" y="20" width="120" height="30" rx="8" fill="#1a3a5f" stroke="#e6b84a" strokeWidth="3" />
      <rect x="78" y="14" width="64" height="14" rx="4" fill="#142c4a" stroke="#e6b84a" strokeWidth="2" />
      <path d="M 40 60 Q 40 50 50 50 L 170 50 Q 180 50 180 60 L 180 200 Q 180 215 165 215 L 55 215 Q 40 215 40 200 Z"
            fill="#ffffff" stroke="#e6b84a" strokeWidth="3" />
      <defs>
        <clipPath id="jarClipVac">
          <path d="M 46 62 L 174 62 L 174 200 Q 174 209 165 209 L 55 209 Q 46 209 46 200 Z" />
        </clipPath>
        <linearGradient id="jarFillVac" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#9bd790" />
          <stop offset="100%" stopColor="#5fa168" />
        </linearGradient>
      </defs>
      <g clipPath="url(#jarClipVac)">
        <motion.rect
          x="46" width="128"
          initial={{ y: Y_BOTTOM, height: 0 }}
          animate={{ y: FILL_Y, height: FILL_HEIGHT }}
          transition={JAR_FILL_TRANSITION}
          fill="url(#jarFillVac)"
        />
        {pct > 0 && (
          <motion.rect
            x="46" width="128" height="3"
            initial={{ y: Y_BOTTOM }}
            animate={{ y: FILL_Y }}
            transition={JAR_FILL_TRANSITION}
            fill="#c4e8b4"
            opacity="0.65"
          />
        )}
      </g>
      <text x="110" y="120" textAnchor="middle"
        fontFamily="Playfair Display" fontStyle="italic" fontSize="20" fill="#1a3a5f">Vacation</text>
      <text x="110" y="138" textAnchor="middle"
        fontFamily="Playfair Display" fontStyle="italic" fontSize="20" fill="#1a3a5f">Fund</text>
      <g className="coin-bob">
        <circle cx="170" cy="135" r="14" fill="#fdf6e7" stroke="#e6b84a" strokeWidth="1.5" />
        <text x="170" y="132" textAnchor="middle" fontFamily="Outfit" fontSize="7" fill="#1a3a5f" letterSpacing="1">SAVED</text>
        <text x="170" y="146" textAnchor="middle" fontFamily="Outfit" fontWeight="700" fontSize="11" fill="#1a3a5f">{Math.round(pct)}%</text>
      </g>
      <g className="coin-bob" style={{ animationDelay: "0.3s" }}>
        <circle cx="190" cy="160" r="9" fill="#f5c64a" stroke="#d4a93a" strokeWidth="1.5" />
        <text x="190" y="164" textAnchor="middle" fontFamily="Outfit" fontWeight="700" fontSize="10" fill="#7a5a10">$</text>
      </g>
      <g className="coin-bob" style={{ animationDelay: "0.6s" }}>
        <circle cx="200" cy="180" r="8" fill="#f5c64a" stroke="#d4a93a" strokeWidth="1.5" />
        <text x="200" y="184" textAnchor="middle" fontFamily="Outfit" fontWeight="700" fontSize="9" fill="#7a5a10">$</text>
      </g>
    </svg>
  );
}

/* ------------------ Itinerary (copied from DreamPage) ------------------ */
const TYPE_META = {
  activity: { Icon: Footprints, color: "#7cc4d6", soft: "#e3f1f5", label: "Activity" },
  restaurant: { Icon: Utensils, color: "#d65d4f", soft: "#fce4e0", label: "Restaurant" },
  note: { Icon: NotebookPen, color: "#a695c7", soft: "#ece6f5", label: "Note" },
};

function ItineraryItemRow({ it, onRemove }) {
  const meta = TYPE_META[it.type] || TYPE_META.note;
  const Icon = meta.Icon;
  return (
    <div data-testid={DREAM.itineraryItem(it.id)} className="flex items-start gap-3 py-2 group">
      <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center"
        style={{ background: meta.soft, color: meta.color }}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-serif-it text-tb-navy text-lg leading-tight">{it.title}</span>
          {it.time && (
            <span className="font-hand text-xs px-2 py-0.5 rounded-full"
              style={{ background: meta.soft, color: meta.color }}>{it.time}</span>
          )}
          {it.url && (
            <a href={it.url} target="_blank" rel="noopener noreferrer"
              className="font-hand text-xs text-tb-coral hover:underline inline-flex items-center gap-0.5">
              link <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
        {it.notes && <div className="font-hand text-tb-navy/70 text-sm leading-snug mt-0.5">{it.notes}</div>}
      </div>
      <button
        data-testid={DREAM.itineraryRemoveItem(it.id)}
        onClick={() => onRemove(it)}
        aria-label={`Remove ${it.title}`}
        className="w-7 h-7 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-tb-navy/40 hover:text-tb-coral hover:bg-tb-coral-soft flex items-center justify-center tb-tap">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function AddItemForm({ day, onAdd, onClose }) {
  const [form, setForm] = useState({ type: "activity", title: "", time: "", notes: "", url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try { await onAdd({ day, ...form }); onClose(); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-2.5 py-1.5 rounded-md bg-tb-cream border border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";
  return (
    <form onSubmit={submit} className="mt-2 p-3 rounded-xl bg-tb-cream-2/50 border-2 border-dashed border-tb-cream-3 space-y-2">
      <div className="flex items-center gap-2">
        <select data-testid={DREAM.itineraryAddType} value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp + " w-32 shrink-0"}>
          <option value="activity">Activity</option>
          <option value="restaurant">Restaurant</option>
          <option value="note">Note</option>
        </select>
        <input data-testid={DREAM.itineraryAddTitle} required value={form.title}
          placeholder="What's the plan?" onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} />
      </div>
      <div className="flex items-center gap-2">
        <input data-testid={DREAM.itineraryAddTime} value={form.time}
          placeholder="time (e.g. dinner)" onChange={(e) => setForm({ ...form, time: e.target.value })} className={inp + " w-36"} />
        <input data-testid={DREAM.itineraryAddUrl} value={form.url}
          placeholder="link (optional)" onChange={(e) => setForm({ ...form, url: e.target.value })} className={inp} />
      </div>
      <input data-testid={DREAM.itineraryAddNotes} value={form.notes}
        placeholder="notes (optional)" onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} />
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onClose}
          className="px-3 py-1.5 rounded-full font-hand text-sm text-tb-navy/70 tb-tap">cancel</button>
        <button data-testid={DREAM.itineraryAddSubmit} disabled={busy} type="submit"
          className="px-4 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap disabled:opacity-60">
          {busy ? "adding…" : "add to day " + day}
        </button>
      </div>
    </form>
  );
}

function Itinerary({ vacation, onChange, vacationId }) {
  const itinerary = vacation.itinerary || [];
  const days = useMemo(() => {
    const ds = new Set(itinerary.map((i) => i.day));
    const max = ds.size ? Math.max(...ds) : 0;
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [itinerary]);
  const [addingDay, setAddingDay] = useState(null);
  const byDay = (n) => itinerary.filter((i) => i.day === n).sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const addItem = async (payload) => {
    try {
      const { data } = await api.post(`/dream/vacations/${vacationId}/itinerary`, payload);
      onChange(data);
      // 2026-07-15 #polish2 — removed "added to your plan" toast;
      // the item appears in the itinerary list.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "could not add");
    }
  };
  const removeItem = async (it) => {
    try {
      const { data } = await api.delete(`/dream/vacations/${vacationId}/itinerary/${it.id}`);
      onChange(data);
    } catch (e) {
      toast.error("could not remove");
    }
  };
  const addNewDay = () => {
    const next = (days.length ? Math.max(...days) : 0) + 1;
    setAddingDay(next);
  };

  return (
    <section data-testid={DREAM.itinerary}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-hand text-tb-navy text-xl">~ your itinerary ~</span>
        <button data-testid={DREAM.itineraryAddDayBtn} onClick={addNewDay}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-sm text-tb-navy/80 tb-tap hover:border-tb-gold">
          <Plus className="w-3.5 h-3.5" /> day
        </button>
      </div>
      {days.length === 0 && addingDay === null && (
        <div className="rounded-2xl border-2 border-dashed border-tb-cream-3 p-5 text-center bg-tb-cream-2/40">
          <p className="font-serif-it text-tb-navy text-lg">Your trip is a blank page.</p>
          <p className="font-hand text-tb-navy/60 text-sm mt-1">Add a day to plot activities, restaurants and notes.</p>
          <button onClick={addNewDay}
            className="mt-3 px-4 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap mx-auto inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> add day 1
          </button>
        </div>
      )}
      <div className="space-y-4">
        {days.map((d) => (
          <div key={d} data-testid={DREAM.itineraryDay(d)}
            className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream px-3 py-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-serif-it text-tb-navy text-xl">Day {d}</div>
              <button data-testid={DREAM.itineraryAddOpen(d)}
                onClick={() => setAddingDay(addingDay === d ? null : d)}
                className="px-3 py-1 rounded-full bg-tb-cream-2 border border-tb-cream-3 font-hand text-xs text-tb-navy/70 tb-tap hover:border-tb-gold flex items-center gap-1">
                {addingDay === d ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                {addingDay === d ? "close" : "add"}
              </button>
            </div>
            <div className="divide-y divide-tb-cream-3/60">
              {byDay(d).length === 0 && addingDay !== d && (
                <p className="font-hand text-tb-navy/50 italic text-sm py-2">
                  nothing planned yet — add an activity or restaurant
                </p>
              )}
              {byDay(d).map((it) => (<ItineraryItemRow key={it.id} it={it} onRemove={removeItem} />))}
            </div>
            {addingDay === d && (<AddItemForm day={d} onAdd={addItem} onClose={() => setAddingDay(null)} />)}
          </div>
        ))}
        {addingDay !== null && !days.includes(addingDay) && (
          <div data-testid={DREAM.itineraryDay(addingDay)}
            className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream px-3 py-3">
            <div className="font-serif-it text-tb-navy text-xl mb-1.5">Day {addingDay}</div>
            <AddItemForm day={addingDay} onAdd={addItem} onClose={() => setAddingDay(null)} />
          </div>
        )}
      </div>
    </section>
  );
}

function CustomAmountField({ subtractMode, onApply }) {
  const [val, setVal] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const n = parseFloat(val);
    if (!n || n <= 0) return;
    onApply(subtractMode ? -n : n);
    setVal("");
  };
  const sign = subtractMode ? "−" : "+";
  const palette = subtractMode
    ? "bg-tb-coral text-white border-tb-coral hover:brightness-110"
    : "bg-tb-navy text-tb-gold-bright border-tb-gold hover:bg-tb-navy-soft";
  return (
    <form onSubmit={submit} className="mt-3 flex items-center justify-center gap-2">
      <div className="flex items-center bg-tb-cream border-2 border-tb-cream-3 focus-within:border-tb-gold rounded-full overflow-hidden">
        <span className="pl-3 pr-1 font-hand text-tb-navy/60 text-base select-none">$</span>
        <input
          data-testid="dream-custom-amount-input"
          inputMode="decimal"
          value={val}
          onChange={(e) => setVal(e.target.value.replace(/[^\d.]/g, ""))}
          placeholder="custom amount"
          className="w-32 py-1.5 pr-2 bg-transparent outline-none font-ui text-tb-navy text-sm"
        />
      </div>
      <button type="submit" data-testid="dream-custom-amount-apply" disabled={!val}
        className={`px-4 py-1.5 rounded-full border-2 font-hand text-sm tb-tap disabled:opacity-50 ${palette}`}>
        {sign}${val || "?"}
      </button>
    </form>
  );
}

/* ------------------ Main page ------------------ */
export default function DreamVacationPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [vacation, setVacation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [subtractMode, setSubtractMode] = useState(false);
  // Item #35 — delete confirmation state.
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const confirmDeleteVacation = async () => {
    setDeleting(true);
    try {
      await api.delete(`/dream/vacations/${id}`);
      toast.success("Dream vacation deleted");
      nav("/dream");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
      setDeleting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dream/vacations/${id}`);
      setVacation(data);
      setGoalInput(String(data.goal_amount || 5000));
      setTitleInput(data.title || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load dream");
      nav("/dream");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const addSaved = async (amount) => {
    try {
      const { data } = await api.post(`/dream/vacations/${id}/save`, { amount });
      setVacation(data);
      // 2026-07-15 #polish2 — removed "+$X added / -$X withdrawn" toast;
      // the jar total on the page updates to reflect the change.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveGoal = async () => {
    const g = parseFloat(goalInput);
    if (!g || g <= 0) return;
    try {
      const { data } = await api.patch(`/dream/vacations/${id}`, { goal_amount: g });
      setVacation(data);
      setEditingGoal(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveTitle = async () => {
    const t = titleInput.trim();
    if (!t) return;
    try {
      const { data } = await api.patch(`/dream/vacations/${id}`, { title: t });
      setVacation(data);
      setEditingTitle(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const bookDream = (d) => {
    if (!d?.affiliate_url) {
      toast.error("No booking link for this destination");
      return;
    }
    window.open(d.affiliate_url, "_blank", "noopener,noreferrer");
  };

  if (loading || !vacation) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="font-hand text-tb-navy/60 text-xl">~ loading your dream ~</span>
      </div>
    );
  }

  const dest = vacation.destination;
  const pct = Math.min(100, ((vacation.saved_amount || 0) / (vacation.goal_amount || 1)) * 100);

  return (
    <div data-testid={DREAM.detailPage} className="flex flex-col">
      <SectionRibbon>build your dream vacation</SectionRibbon>

      <div className="px-6 md:px-10 lg:px-12 py-6 bg-white">
        <div className="flex items-center justify-between mb-3">
          <Link to="/dream" data-testid={DREAM.backToList}
            className="flex items-center gap-1.5 font-hand text-tb-gold-soft hover:text-tb-gold-bright tb-tap text-sm md:text-base">
            <ArrowLeft className="w-4 h-4" /> <Star className="w-4 h-4" fill="currentColor" strokeWidth={1.4} /> build your dream vacation
          </Link>
          {/* Item #35 — delete button in the detail header. Ghost style
              matches the back link on the left. */}
          <button
            data-testid={DREAM.detailDeleteBtn}
            onClick={() => setShowDelete(true)}
            aria-label="Delete this dream vacation"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 hover:border-tb-coral hover:bg-tb-coral-soft text-tb-navy/70 hover:text-tb-coral font-hand text-sm tb-tap"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">delete dream</span>
          </button>
        </div>

        {/* v10.5 — Hero image card with overlay title, dates, and
            "In Progress" status pill. Editable title via pencil icon
            uses the existing PATCH endpoint (no new backend). */}
        <div className="relative rounded-2xl overflow-hidden shadow-md" style={{ minHeight: 320 }}>
          <img
            src={vacation.cover_image_url || "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=1600&q=80"}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-tb-navy-deep/85 via-tb-navy-deep/30 to-transparent" />
          <div className="relative p-6 md:p-8 flex flex-col justify-end min-h-[320px]">
            {editingTitle ? (
              <div className="flex items-center gap-2 max-w-xl">
                <input
                  data-testid={DREAM.titleInput}
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                  className="flex-1 px-3 py-2 rounded-lg bg-white/95 border-2 border-tb-gold outline-none font-serif-it text-tb-navy text-2xl md:text-3xl"
                />
                <button data-testid={DREAM.saveTitleBtn} onClick={saveTitle}
                  className="w-10 h-10 rounded-full bg-tb-gold text-tb-navy-deep tb-tap flex items-center justify-center">
                  <Check className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                data-testid={DREAM.titleEdit}
                onClick={() => setEditingTitle(true)}
                className="text-left flex items-center gap-2 group tb-tap"
              >
                <h1 className="font-serif-it italic text-white text-3xl md:text-4xl leading-tight drop-shadow-lg">
                  {vacation.title}{vacation.country ? `, ${vacation.country}` : ""}
                </h1>
                <Pencil className="w-4 h-4 md:w-5 md:h-5 text-white/70 group-hover:text-tb-gold-bright transition-colors" />
              </button>
            )}
            <span className="mt-3 inline-flex self-start px-3 py-1 rounded-full bg-white/15 border border-tb-gold text-tb-gold-bright text-xs uppercase tracking-wider font-hand">
              {pct >= 100 ? "Ready to Book" : pct > 0 ? "In Progress" : "Planned"}
            </span>
          </div>
        </div>

        {/* v10.5 — Vacation Fund card with SVG donut gauge + amount +
            progress bar. Replaces the legacy jar visual. Reuses the
            same `/save` and PATCH endpoints. */}
        <div className="mt-6 rounded-2xl bg-white border-2 border-tb-gold shadow-[0_10px_28px_-16px_rgba(230,184,74,0.4)] p-6 md:p-8" data-testid={DREAM.jar}>
          <div className="flex items-center gap-6">
            <div className="flex-1 min-w-0">
              <p className="font-hand uppercase tracking-widest text-xs text-tb-navy/55">Vacation Fund</p>
              <div data-testid={DREAM.savedAmount} className="mt-1 font-serif-it text-tb-navy text-4xl md:text-5xl leading-none">
                ${(vacation.saved_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              {editingGoal ? (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="font-hand text-tb-navy/70">goal $</span>
                  <input value={goalInput} onChange={(e) => setGoalInput(e.target.value)}
                    type="number"
                    className="w-24 px-2 py-1 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-tb-navy" />
                  <button onClick={saveGoal} className="font-hand text-tb-blue underline">save</button>
                </div>
              ) : (
                <button onClick={() => setEditingGoal(true)} className="mt-1 font-hand text-tb-navy/60 text-sm md:text-base hover:text-tb-coral">
                  of ${(vacation.goal_amount || 0).toLocaleString()} goal &middot; {Math.round(pct)}% there! &#9998;
                </button>
              )}
              <div className="mt-3 h-2 bg-tb-cream-2 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-tb-gold to-tb-gold-bright rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            {/* Circular gauge — SVG donut with center label as absolute
                positioned overlay (SVG <text> alignment is unreliable
                after CSS rotation). */}
            <div className="relative w-24 h-24 md:w-28 md:h-28 shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#fdf6e7" strokeWidth="10" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#e6b84a" strokeWidth="10"
                  strokeDasharray={`${(pct / 100) * 2 * Math.PI * 42} ${2 * Math.PI * 42}`}
                  strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="font-serif-it italic text-tb-navy text-lg md:text-xl">
                  {Math.round(pct)}%
                </span>
              </div>
            </div>
          </div>

          {/* Quick-add/subtract buttons (unchanged logic) */}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              data-testid={DREAM.subtractToggle}
              onClick={() => setSubtractMode((m) => !m)}
              aria-pressed={subtractMode}
              className={"w-10 h-10 rounded-full border-2 flex items-center justify-center tb-tap " +
                (subtractMode ? "bg-tb-coral text-white border-tb-coral" : "bg-tb-cream-2 text-tb-coral border-tb-coral/40 hover:bg-tb-coral-soft")}>
              <Minus className="w-4 h-4" />
            </button>
            {QUICK_ADD.map((amt) => {
              const signed = subtractMode ? -amt : amt;
              const testId = subtractMode ? DREAM.subtractAmount(amt) : DREAM.addAmount(amt);
              return (
                <button key={amt} data-testid={testId} onClick={() => addSaved(signed)}
                  className={"px-4 py-2 rounded-2xl border font-ui font-semibold tb-tap " +
                    (subtractMode
                      ? "bg-tb-coral-soft border-tb-coral/40 text-tb-coral hover:bg-tb-coral hover:text-white"
                      : "bg-tb-cream-2 border-tb-cream-3 text-tb-navy hover:bg-tb-gold-bright hover:border-tb-gold")}>
                  {subtractMode ? "\u2212" : "+"}${amt}
                </button>
              );
            })}
          </div>
          <CustomAmountField subtractMode={subtractMode} onApply={addSaved} />
        </div>

        {/* Book Now — full-width navy CTA if a destination is linked */}
        {dest && dest.affiliate_url && (
          <div className="mt-6">
            <button
              data-testid="dream-book-now-btn"
              onClick={() => bookDream(dest)}
              className="w-full h-14 rounded-2xl bg-tb-navy-deep text-white font-semibold text-lg shadow hover:brightness-110 transition tb-tap flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              Book {dest.name || vacation.title} now
            </button>
            <p className="mt-2 text-center font-hand text-tb-navy/55 italic text-xs">
              TravelBio may receive a commission from booking through this link.
            </p>
            <p className="mt-1 text-center font-hand text-tb-gold-soft text-sm underline">
              {pct >= 100 ? "🎉 fund is full — secure your trip!" : "Ready to book early? Secure the deal now."}
            </p>
          </div>
        )}

        <div className="border-t border-dashed border-tb-cream-3 my-8" />

        {/* Pin Board (existing component, unchanged) */}
        <PinBoard
          pins={vacation.pins || []}
          onChange={setVacation}
          endpoints={{
            create: `/dream/vacations/${id}/pins`,
            remove: (pin) => `/dream/vacations/${id}/pins/${pin.id}`,
          }}
        />

        <div className="border-t border-dashed border-tb-cream-3 my-8" />

        {/* Itinerary (existing component, unchanged) */}
        <Itinerary vacation={vacation} onChange={setVacation} vacationId={id} />

        <p className="text-center font-hand text-tb-navy/50 italic text-sm mt-8">
          keep dreaming, keep saving ✦
        </p>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-4">
          <div data-testid={DREAM.deleteConfirmDialog} role="dialog" aria-modal="true"
            className="bg-tb-cream rounded-2xl border-2 border-tb-coral max-w-md w-full">
            <div className="bg-tb-coral text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <span className="font-hand text-xl">delete this dream?</span>
              <button type="button" onClick={() => !deleting && setShowDelete(false)}
                className="font-hand text-white/85 tb-tap">close</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="font-serif-it italic text-tb-navy text-xl leading-snug">
                {vacation.title || "Untitled dream"}
                {vacation.country ? `, ${vacation.country}` : ""}
              </p>
              <p className="font-hand text-tb-navy/70 text-sm">
                This clears the money jar, the pin board and the itinerary you saved
                for this vacation. It can't be undone.
              </p>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button type="button" disabled={deleting}
                  data-testid={DREAM.deleteConfirmCancel}
                  onClick={() => setShowDelete(false)}
                  className="px-4 py-2 rounded-full font-hand text-sm text-tb-navy/70 tb-tap">
                  cancel
                </button>
                <button type="button" disabled={deleting}
                  data-testid={DREAM.deleteConfirmSubmit}
                  onClick={confirmDeleteVacation}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-coral text-white border-2 border-tb-coral font-hand text-sm tb-tap disabled:opacity-60">
                  <Trash2 className="w-3.5 h-3.5" />
                  {deleting ? "deleting…" : "delete dream"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
